/**
 * F19.3 — bind the MSAL lifecycle to the Clerk session lifecycle.
 *
 * Without this, MSAL state persists indefinitely across Clerk sign-out:
 *   - User A signs in with Clerk → MSAL pops up → tokens cached in
 *     sessionStorage AND accounts persisted in MSAL's internal store.
 *   - User A signs out of Clerk → MSAL state untouched.
 *   - User B signs in with Clerk on the same browser → MSAL still
 *     "remembers" User A's account; any silent token call resolves
 *     against User A's identity.
 *
 * The standard cure (and what F19 ADR-M / D13 locked) is to listen
 * for Clerk identity transitions and clear MSAL state on each
 * transition. The two exports below — `useMsalSessionBinding` (hook)
 * and `subscribeMsalToClerkSession` (non-hook) — are the single
 * chokepoint that does it. Earlier F19 design notes called this
 * `bindMsalToClerk`; that name was never adopted in code.
 *
 * Why a separate file from msalClient.ts:
 *   - msalClient.ts has zero dependency on Clerk (it's framework-
 *     agnostic). Keeping the Clerk wiring in a sibling preserves that
 *     so tests/smokes can use msalClient without pulling in
 *     `@clerk/react`.
 *
 * Why a React hook variant:
 *   - The SPA mount point lives inside `<ClerkProvider>` so React
 *     hooks are the natural way to subscribe to Clerk identity
 *     changes. The non-hook variant `subscribeMsalToClerkSession` is
 *     also exported for code paths that observe Clerk via vanilla JS.
 *
 * Decisions: D13 / ADR-M.
 */

import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';
import { clearMsalSession, type ClearMsalSessionResult } from './msalClient';

/**
 * Hook variant — call once near the SPA root inside `<ClerkProvider>`.
 * Watches `useUser().user?.id` (the Clerk subject) and triggers
 * `clearMsalSession` whenever it transitions:
 *
 *   - Initial null → first signed-in id: no-op (no prior session to clear).
 *   - Signed-in id → null: SIGN-OUT — clear MSAL + drop repancy: keys.
 *   - Signed-in id A → signed-in id B: USER SWAP — clear MSAL + drop repancy: keys.
 *
 * Idempotent and side-effect-free until a real transition happens.
 *
 * The `onClear` callback fires with the result of every clear pass so
 * a smoke / e2e harness can assert via a test hook. F19.6 will register
 * a callback here to rotate `repancy:demoSessionId` (the single key the
 * default `clearMsalSession` preserves; F19.6 wires the rotation).
 */
export function useMsalSessionBinding(opts?: {
  onClear?: (result: ClearMsalSessionResult, transition: SessionTransition) => void;
  preserveKeys?: ReadonlyArray<string>;
}): void {
  const { user, isLoaded } = useUser();
  const lastSubjectRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!isLoaded) return;
    const currentSubject = user?.id ?? null;
    const lastSubject = lastSubjectRef.current;
    // `undefined` = first observation — record without clearing.
    if (lastSubject === undefined) {
      lastSubjectRef.current = currentSubject;
      return;
    }
    if (lastSubject === currentSubject) return;
    const transition: SessionTransition =
      lastSubject !== null && currentSubject === null
        ? 'sign-out'
        : lastSubject === null && currentSubject !== null
          ? 'sign-in'
          : 'user-swap';
    lastSubjectRef.current = currentSubject;
    // Sign-in (first identity from null) was traditionally NOT a clear
    // event, but Clerk re-mounts can land users on a fresh tab where a
    // PRIOR user's MSAL state survived in sessionStorage; clearing on
    // sign-in too means the first thing the user sees is a clean MSAL
    // state. Sign-out + user-swap are the cases the F19 plan explicitly
    // requires.
    if (transition === 'sign-in') return;
    void clearMsalSession({ preserveKeys: opts?.preserveKeys }).then(
      (result) => opts?.onClear?.(result, transition),
    );
  }, [isLoaded, user, opts?.onClear, opts?.preserveKeys]);
}

export type SessionTransition = 'sign-in' | 'sign-out' | 'user-swap';

/**
 * Non-hook variant — subscribe a callback to a manually-provided
 * stream of Clerk-subject changes. Useful for code paths that observe
 * Clerk via vanilla JS (e.g. a Convex-side reactor) or for unit tests
 * that want to drive transitions deterministically.
 *
 * Returns an `unsubscribe` function. Calls `clearMsalSession` on
 * sign-out and user-swap; no-op on initial sign-in.
 */
export function subscribeMsalToClerkSession(
  source: ClerkSubjectSource,
  opts?: {
    onClear?: (result: ClearMsalSessionResult, transition: SessionTransition) => void;
    preserveKeys?: ReadonlyArray<string>;
  },
): () => void {
  let lastSubject: string | null | undefined = undefined;
  const handler = (subject: string | null): void => {
    if (lastSubject === undefined) {
      lastSubject = subject;
      return;
    }
    if (lastSubject === subject) return;
    const transition: SessionTransition =
      lastSubject !== null && subject === null
        ? 'sign-out'
        : lastSubject === null && subject !== null
          ? 'sign-in'
          : 'user-swap';
    lastSubject = subject;
    if (transition === 'sign-in') return;
    void clearMsalSession({ preserveKeys: opts?.preserveKeys }).then(
      (result) => opts?.onClear?.(result, transition),
    );
  };
  return source.subscribe(handler);
}

export interface ClerkSubjectSource {
  subscribe: (handler: (subject: string | null) => void) => () => void;
}
